import BasePlugin from '@uppy/core/lib/BasePlugin.js';

/**
 * A video thumbnail generator plugin for Uppy.
 */
export default class VideoThumbnailGenerator extends BasePlugin {
  constructor(uppy, options) {
    super(uppy);

    this.id = 'VideoThumbnailGenerator';
    this.title = 'Video Thumbnail Generator';
    this.type = 'modifier';
    this.queue = [];
    this.isProcessingQueue = false;
    this.options = Object.assign({ thumbnailWidth: 200 }, options);
    this.onFileAdded = this.onFileAdded.bind(this);
  }

  /**
   * Determines whether a file type belongs to the video category.
   *
   * @param {String} fileType The type of the file.
   * @returns {Boolean} Whether the type belongs to the video category.
   */
  isVideo(fileType) {
    return fileType.split('/')[0] === 'video';
  }

  /**
   * Gets the proportionally scaled dimensions according to the configured width.
   *
   * @param {Number} width The original width.
   * @param {Number} height The original height.
   * @returns {Object} The new dimensions.
   */
  getScaledDimensions(width, height) {
    return {
      width: this.options.thumbnailWidth,
      height: height * this.options.thumbnailWidth / width,
    };
  }

  /**
   * The install instructions for the plugin.
   */
  install() {
    this.uppy.on('file-added', this.onFileAdded);
  }

  /**
   * The uninstall instructions for the plugin.
   */
  uninstall() {
    this.uppy.off('file-added', this.onFileAdded);
  }

  /**
   * The handler for the 'file-added' event.
   *
   * @param {Object} file The Uppy file creation object.
   */
  onFileAdded(file) {
    if (!this.isVideo(file.type)) return;

    this.queue.push(file);

    if (this.isProcessingQueue === false) {
      this.processQueue();
    }
  }

  /**
   * Recursively processes items in the queue until it is empty.
   */
  processQueue() {
    if (!this.queue.length) {
      this.isProcessingQueue = false;
      this.uppy.log(`[${this.title}] Emptied video thumbnail queue`);
      this.uppy.emit('video-thumbnail:all-generated');
      return;
    }

    this.isProcessingQueue = true;
    const currentFile = this.queue.shift();
    this.requestThumbnail(currentFile).then(() => this.processQueue());
  }

  /**
   * Requests a thumbnail creation and handles the success or failure.
   *
   * @param {Object} file The Uppy file creation object.
   * @param {File} file.data The actual file object.
   * @param {String} file.id The file's Uppy ID.
   * @returns {Promise}
   */
  requestThumbnail(file) {
    return this.createThumbnail(file.data)
      .then((preview) => {
        this.uppy.setFileState(file.id, { preview });
        this.uppy.log(`[${this.title}] Generated thumbnail for ${file.id}`);
        this.uppy.emit('video-thumbnail:generated', this.uppy.getFile(file.id), preview);
      })
      .catch((error) => {
        this.uppy.log(`[${this.title}] Failed thumbnail for ${file.id}:`, 'warning');
        this.uppy.log(error, 'warning');
        this.uppy.emit('video-thumbnail:error', this.uppy.getFile(file.id), error);
      });
  }

  /**
   * Generates a video thumbnail using canvas.
   *
   * Overview of the thumbnail generation process:
   *
   * 1. An object URL is created to reference the video file data in memory.
   * 2. A video element and canvas element are created.
   * 3. The video source is set to the object URL and the video is played.
   * 4. The video emits `timeupdate`, so we draw a proportially-scaled video element on the canvas.
   * 5. The canvas is converted to a blob, and we return an object URL that points to it.
   * 6. The video is unloaded.
   *
   * @param {File} file The file to create a thumbnail for.
   * @returns {Promise} The URL of the thumbnail.
   */
  createThumbnail(file) {
    const url = URL.createObjectURL(file);
    const video = document.createElement('video');
    const canvas = document.createElement('canvas');

    video.playsInline = true;
    video.muted = true;
    video.src = url;

    return video.play()
      .then(() => {
        return new Promise((resolve, reject) => {
          video.addEventListener('timeupdate', () => resolve(), { once: true });
          video.addEventListener('error', (event) => reject(event.target.error), { once: true });
        });
      })
      .then(() => {
        const { width, height } = this.getScaledDimensions(video.videoWidth, video.videoHeight);
        canvas.width = width;
        canvas.height = height;
        canvas.getContext('2d').drawImage(video, 0, 0, width, height);
        return this.getCanvasBlob(canvas);
      })
      .then((blob) => {
        return URL.createObjectURL(blob);
      })
      .finally(() => {
        video.pause();
        video.removeAttribute('src');
        video.load();
        URL.revokeObjectURL(url);
      });
  }

  /**
   * Converts a canvas into a blob.
   *
   * @param {Canvas} canvas The canvas object to convert to a blob.
   * @returns {Blob} The newly created blob.
   */
  getCanvasBlob(canvas) {
    return new Promise((resolve, reject) => {
      canvas.toBlob((blob) => {
        if (blob === null) {
          reject(new Error('Failed to create blob from canvas'));
          return;
        }

        resolve(blob);
      });
    });
  }
}
